import { useEffect, useState } from "react";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Check, Send, Ban, Pencil, Zap, Trash2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(tz);

const LONDON = "Europe/London";
// Convert stored UTC ISO to <input type="datetime-local"> value in London
const isoToLocalInput = (iso) => iso ? dayjs.utc(iso).tz(LONDON).format("YYYY-MM-DDTHH:mm") : "";
// Convert <input type="datetime-local"> London value back to UTC ISO
const localInputToIso = (v) => v ? dayjs.tz(v, LONDON).utc().toISOString() : null;

export default function Reminders() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending_approval");
  const [editor, setEditor] = useState(null); // reminder being previewed/edited
  const [savingEdit, setSavingEdit] = useState(false);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = async () => {
    const params = status !== "all" ? { status } : {};
    const [r, s] = await Promise.all([api.get("/reminders", { params }), api.get("/settings")]);
    setRows(r.data);
    setSettings(s.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const approve = async (id) => { await api.post(`/reminders/${id}/approve`); toast.success("Approved"); load(); };
  const cancel = async (id) => { await api.post(`/reminders/${id}/cancel`); toast.success("Cancelled"); load(); };
  const send = async (id) => {
    try {
      const r = await api.post(`/reminders/${id}/send`);
      if (r.data.status === "sent") toast.success("Sent (mocked)");
      else if (r.data.status === "blocked") toast.error(`Blocked: ${r.data.reason}`);
      else toast.error(`Failed: ${r.data.reason || "unknown"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const runAll = async () => {
    const r = await api.post("/reminders/run-due");
    toast.success(`Engine ran — ${r.data.sent} sent, ${r.data.blocked} blocked`);
    load();
  };

  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAllOnPage = () => setSelected((s) => {
    const allIds = rows.map((r) => r.reminder_id);
    const allSelected = allIds.every((id) => s.has(id));
    if (allSelected) { const n = new Set(s); allIds.forEach((id) => n.delete(id)); return n; }
    const n = new Set(s); allIds.forEach((id) => n.add(id)); return n;
  });
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const r = await api.post("/reminders/bulk-delete", { ids: Array.from(selected) });
      const skipped = r.data.sent_deleted || 0;
      toast.success(`Deleted ${r.data.deleted} reminder${r.data.deleted === 1 ? "" : "s"}${skipped ? ` · incl. ${skipped} sent (audit-logged)` : ""}`);
      clearSelection();
      setBulkDeleteOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally { setBulkDeleting(false); }
  };

  return (
    <div>
      <PageHeader
        testid="page-reminders"
        title="Reminder queue"
        subtitle={settings ? `Reminders go to you · ${settings.admin_test_email}${settings.test_mode ? " · Review mode" : " · Live"}` : "Loading…"}
        actions={<Button data-testid="run-engine-btn" onClick={runAll} className="rounded-md bg-slate-900 hover:bg-slate-800"><Zap className="h-4 w-4 mr-2" /> Run engine</Button>}
      />
      <div className="p-6 lg:p-8 space-y-4">
        <div className="flex items-center gap-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="reminders-status-filter" className="w-52 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending_approval">Pending approval</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-sm text-slate-500">{rows.length} reminder(s)</div>
        </div>

        <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
          {selected.size > 0 && (
            <div data-testid="reminders-bulk-bar" className="bg-slate-900 text-slate-100 px-4 py-2 flex items-center justify-between text-sm">
              <span data-testid="reminders-selected-count">
                <span className="font-semibold">{selected.size}</span> selected
              </span>
              <div className="flex items-center gap-3">
                <button type="button" onClick={clearSelection} className="text-slate-300 hover:text-white text-xs" data-testid="reminders-clear-selection">Clear</button>
                <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)} className="h-8" data-testid="reminders-bulk-delete-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete selected
                </Button>
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.1em] text-slate-500">
              <tr>
                <th className="w-10 px-4 py-2">
                  <Checkbox
                    data-testid="reminders-select-all"
                    checked={rows.length > 0 && rows.every((r) => selected.has(r.reminder_id))}
                    onCheckedChange={toggleAllOnPage}
                    aria-label="Select all reminders on this page"
                  />
                </th>
                <th className="text-left px-4 py-2">Client · Job</th>
                <th className="text-left px-4 py-2">Trigger</th>
                <th className="text-left px-4 py-2">Scheduled</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No reminders match this filter.</td></tr>}
              {rows.map((r) => (
                <tr key={r.reminder_id} data-testid="reminder-row" className={`border-t border-slate-100 hover:bg-slate-50 transition-colors ${selected.has(r.reminder_id) ? "bg-slate-50" : ""}`}>
                  <td className="px-4 py-3">
                    <Checkbox
                      data-testid={`reminder-row-select-${r.reminder_id}`}
                      checked={selected.has(r.reminder_id)}
                      onCheckedChange={() => toggleOne(r.reminder_id)}
                      aria-label={`Select reminder for ${r.job_title}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{r.job_title}</div>
                    <div className="text-xs text-slate-500">{r.client_name}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">T-{r.trigger_days_before}d</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDateTime(r.scheduled_at)}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button data-testid="edit-btn" onClick={() => setEditor({ ...r })} title="Preview / edit" className="p-1.5 text-slate-500 hover:text-slate-900 rounded transition-colors"><Pencil className="h-4 w-4" /></button>
                      {r.status === "pending_approval" && (
                        <button data-testid="approve-btn" onClick={() => approve(r.reminder_id)} title="Approve" className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"><Check className="h-4 w-4" /></button>
                      )}
                      {r.status !== "sent" && r.status !== "cancelled" && (
                        <>
                          <button data-testid="send-btn" onClick={() => send(r.reminder_id)} title="Send now" className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"><Send className="h-4 w-4" /></button>
                          <button data-testid="cancel-btn" onClick={() => cancel(r.reminder_id)} title="Cancel" className="p-1.5 text-slate-500 hover:text-rose-600 rounded transition-colors"><Ban className="h-4 w-4" /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent data-testid="reminders-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} reminder{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Pending, approved, blocked, failed and cancelled reminders will be removed. Any
              already-sent reminders in your selection are kept for audit. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={bulkDelete}
              disabled={bulkDeleting}
              data-testid="reminders-bulk-delete-confirm"
              className="bg-rose-600 hover:bg-rose-700"
            >
              {bulkDeleting ? "Deleting…" : `Yes, delete ${selected.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editor} onOpenChange={() => setEditor(null)}>
        <DialogContent data-testid="reminder-editor" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{(editor?.status === "sent" || editor?.status === "cancelled") ? "Reminder (read-only)" : "Edit reminder"}</DialogTitle>
          </DialogHeader>
          {editor && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-4 gap-2 items-center">
                <div className="text-slate-500">To</div>
                <div className="col-span-3 font-mono text-xs">{editor.to_email || "—"} {editor.to_name && <span className="text-slate-400">({editor.to_name})</span>}</div>
                <div className="text-slate-500">Client · Job</div>
                <div className="col-span-3">{editor.client_name} · {editor.job_title}</div>
                <div className="text-slate-500">Trigger</div>
                <div className="col-span-3 font-mono text-xs">T-{editor.trigger_days_before} days before deadline</div>
              </div>
              <div>
                <Label>Scheduled send date/time <span className="text-slate-400">(Europe/London)</span></Label>
                <Input
                  data-testid="reminder-scheduled-input"
                  type="datetime-local"
                  value={isoToLocalInput(editor.scheduled_at)}
                  onChange={(e) => setEditor({ ...editor, scheduled_at: localInputToIso(e.target.value) })}
                  disabled={editor.status === "sent" || editor.status === "cancelled"}
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Currently: <span className="font-mono">{fmtDateTime(editor.scheduled_at)}</span>
                </div>
              </div>
              <div>
                <Label>Subject</Label>
                <Input
                  data-testid="reminder-subject-input"
                  value={editor.subject || ""}
                  onChange={(e) => setEditor({ ...editor, subject: e.target.value })}
                  disabled={editor.status === "sent" || editor.status === "cancelled"}
                />
              </div>
              <div>
                <Label>Body</Label>
                <Textarea
                  data-testid="reminder-body-input"
                  rows={10}
                  value={editor.body || ""}
                  onChange={(e) => setEditor({ ...editor, body: e.target.value })}
                  disabled={editor.status === "sent" || editor.status === "cancelled"}
                />
              </div>
              {settings?.test_mode && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
                  Review mode — subject prefixed with [Taxegy review] and delivered via Resend to your practice inbox.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {editor && editor.status !== "sent" && editor.status !== "cancelled" && (
              <Button
                data-testid="save-reminder-btn"
                disabled={savingEdit}
                onClick={async () => {
                  setSavingEdit(true);
                  try {
                    await api.patch(`/reminders/${editor.reminder_id}`, {
                      scheduled_at: editor.scheduled_at,
                      subject: editor.subject,
                      body: editor.body,
                    });
                    toast.success("Reminder updated");
                    setEditor(null);
                    load();
                  } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  finally { setSavingEdit(false); }
                }}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {savingEdit ? "Saving…" : "Save changes"}
              </Button>
            )}
            <Button onClick={() => setEditor(null)} variant="outline">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
